import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, chatAPI, providersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ArrowLeft, Send, Coins, FileText, Code2, TestTube, ListChecks,
  Bot, User, Terminal, GitBranch, Layers, Palette,
  Database, Cpu, Plus, Trash2, Save, Download, Eye, Sun, Moon,
  PanelLeft, PanelRight, ChevronDown, ChevronRight, Workflow,
  BarChart3, ClipboardList, Network, Settings, FolderGit2, Unplug,
  LayoutGrid, Bug, Milestone, Users, X, Lock, Shield, Target,
  RefreshCw, AlertOctagon, Tag, ClipboardCheck, Sparkles, Upload,
  Filter, FolderTree, GitCommit, FileUp, CheckCircle2, Edit2, Zap,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import MermaidDiagram from '../components/MermaidDiagram';
import CodePreview from '../components/CodePreview';
import ModernChat from '../components/ModernChat';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { ProcessingIndicator, BarShimmerLoader, DotPulseLoader } from '../components/ModernLoader';
import TasksPanel from './TasksPanel';
import BugTrackerPanel from './BugTrackerPanel';
import RoadmapPanel from './RoadmapPanel';
import MembersPanel from './MembersPanel';
import JiraPanel from './JiraPanel';
import ChangeRequestsPanel from './ChangeRequestsPanel';
import ShowstoppersPanel from './ShowstoppersPanel';
import ReleaseNotesPanel from './ReleaseNotesPanel';
import TestCasesPanel from './TestCasesPanel';
import NewFeaturesPanel from './NewFeaturesPanel';
import SprintsPanel from './SprintsPanel';
import MeetingsPanel from './MeetingsPanel';

const mainPhases = [
  {
    id: 'project_specs_group', label: 'Project Specs', icon: FolderTree, expandable: true,
    subPages: [
      { id: 'executive_summary', label: 'Executive Summary', icon: FileText },
      { id: 'scope', label: 'Project Scope', icon: Target },
      {
        id: 'requirements_group', label: 'Requirements', icon: ListChecks, expandable: true,
        subPages: [
          { id: 'req_list', label: 'Requirements List', icon: ClipboardList },
          { id: 'usecase_diagram', label: 'Use Case Diagram', icon: Workflow },
          { id: 'rsd', label: 'RSD', icon: FileText },
          { id: 'frs', label: 'FRS', icon: BarChart3 },
          { id: 'fsd', label: 'FSD', icon: Layers },
        ]
      },
      { id: 'design', label: 'Design', icon: Palette },
      { id: 'architecture', label: 'Architecture', icon: Cpu },
      { id: 'domain', label: 'Domain Model', icon: Database },
      { id: 'technology', label: 'Technology', icon: Terminal },
    ]
  },
  { id: 'code', label: 'Code', icon: Code2 },
  { id: 'tests', label: 'Testing', icon: TestTube },
];

const managementPhases = [
  { id: 'meetings', label: 'Meetings', icon: Calendar },
  { id: 'sprints', label: 'Sprints', icon: Zap },
  { id: 'tasks', label: 'Tasks', icon: LayoutGrid },
  { id: 'bugs', label: 'Bug Tracker', icon: Bug },
  { id: 'roadmap', label: 'Roadmap', icon: Milestone },
  { id: 'members', label: 'Team', icon: Users },
  { id: 'change_requests', label: 'Change Requests', icon: RefreshCw },
  { id: 'showstoppers', label: 'Showstoppers', icon: AlertOctagon },
  { id: 'release_notes', label: 'Release Notes', icon: Tag },
  { id: 'test_cases', label: 'Test Cases', icon: ClipboardCheck },
  { id: 'new_features', label: 'New Features', icon: Sparkles },
  { id: 'jira', label: 'Jira', icon: Settings },
];

const settingsPhases = [
  { id: 'source_control', label: 'Source Control', icon: FolderGit2 },
  { id: 'project_settings', label: 'Settings', icon: Settings },
];

// RBAC: Define which phases each role can access
const rolePermissions = {
  owner: ['all'],
  admin: ['all'],
  developer: ['requirements', 'architecture', 'domain', 'technology', 'code', 'tests', 'tasks', 'bugs'],
  designer: ['requirements', 'design', 'tasks'],
  qa: ['requirements', 'tests', 'tasks', 'bugs'],
  ba: ['requirements', 'design', 'tasks', 'roadmap'],
  viewer: ['view_only'],
};

// Map phases to their permission category
const phaseToPermission = {
  'executive_summary': 'requirements', 'req_list': 'requirements', 'scope': 'requirements',
  'usecase_diagram': 'requirements', 'rsd': 'requirements', 
  'frs': 'requirements', 'fsd': 'requirements', 'design': 'design', 'architecture': 'architecture',
  'domain': 'domain', 'technology': 'technology', 'code': 'code', 'tests': 'tests',
  'tasks': 'tasks', 'bugs': 'bugs', 'roadmap': 'roadmap', 'members': 'members', 'jira': 'jira',
  'change_requests': 'tasks', 'showstoppers': 'bugs', 'release_notes': 'tasks',
  'test_cases': 'tests', 'new_features': 'tasks', 'meetings': 'tasks', 'sprints': 'tasks',
  'source_control': 'settings', 'project_settings': 'settings'
};

const techOptions = {
  frontend: ['React', 'Next.js', 'Vue.js', 'Angular', 'Svelte', 'Flutter', 'React Native', 'Swift', 'Kotlin', 'HTML/CSS/JS', 'None'],
  backend: ['Node.js', 'Python/FastAPI', 'Python/Django', 'Java/Spring', 'Go', 'Rust', 'PHP/Laravel', 'Ruby/Rails', 'C#/.NET', 'None'],
  database: ['PostgreSQL', 'MySQL', 'MongoDB', 'SQLite', 'Redis', 'DynamoDB', 'Firestore', 'Supabase', 'CockroachDB', 'None'],
  cloud: ['AWS', 'Google Cloud', 'Azure', 'Vercel', 'Railway', 'DigitalOcean', 'Heroku', 'Self-hosted', 'None'],
  devops: ['Docker', 'Kubernetes', 'GitHub Actions', 'Jenkins', 'Terraform', 'AWS CDK', 'None'],
};

export default function ProjectWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser, theme, setTheme } = useAuth();
  const [project, setProject] = useState(null);
  const [activePhase, setActivePhase] = useState('scope');
  const [reqGroupExpanded, setReqGroupExpanded] = useState(true);
  const [projectSpecsExpanded, setProjectSpecsExpanded] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [newReqText, setNewReqText] = useState('');
  const [newReqCategory, setNewReqCategory] = useState('functional');
  const [newReqPriority, setNewReqPriority] = useState('medium');
  const [generatingDoc, setGeneratingDoc] = useState(null);
  const [techStack, setTechStack] = useState({ frontend: '', backend: '', database: '', cloud: '', devops: '', other: '' });
  // Source control
  const [scProvider, setScProvider] = useState('github');
  const [scRepoUrl, setScRepoUrl] = useState('');
  const [scBranch, setScBranch] = useState('main');
  // Project settings
  const [providers, setProviders] = useState([]);
  const [settingsForm, setSettingsForm] = useState({ name: '', description: '', llm_provider: '', llm_model: '' });
  // Task management data
  const [members, setMembers] = useState([]);
  const [epics, setEpics] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [userRole, setUserRole] = useState('owner');

  const chatEndRef = useRef(null);

  const loadProject = useCallback(async () => {
    try {
      const res = await projectAPI.get(id);
      setProject(res.data);
      if (res.data.phases?.technology?.stack) setTechStack(res.data.phases.technology.stack);
      if (res.data.source_control) {
        setScProvider(res.data.source_control.provider || 'github');
        setScRepoUrl(res.data.source_control.repo_url || '');
        setScBranch(res.data.source_control.branch || 'main');
      }
      setSettingsForm({ name: res.data.name, description: res.data.description || '', llm_provider: res.data.llm_provider || 'openai', llm_model: res.data.llm_model || 'gpt-5.2' });
      
      // Load members, epics, tasks
      try {
        const [membersRes, epicsRes, tasksRes] = await Promise.all([
          projectAPI.listMembers(id),
          projectAPI.listEpics(id),
          projectAPI.listTasks(id)
        ]);
        setMembers(membersRes.data || []);
        setEpics(epicsRes.data || []);
        setTasks(tasksRes.data || []);
        
        // Determine user role
        const currentMember = membersRes.data.find(m => m.user_id === user?.id);
        if (res.data.user_id === user?.id) {
          setUserRole('owner');
        } else if (currentMember) {
          setUserRole(currentMember.role);
        } else {
          setUserRole('viewer');
        }
      } catch (e) {
        console.log('Failed to load project data', e);
      }
    } catch {
      toast.error('Project not found');
      navigate('/dashboard');
    } finally { setLoading(false); }
  }, [id, navigate, user?.id]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [project?.chat_history, activePhase]);
  useEffect(() => {
    if (activePhase === 'project_settings') providersAPI.listActive().then(res => setProviders(res.data)).catch(() => {});
  }, [activePhase]);

  // Reload tasks
  const loadTasks = async () => {
    try {
      const res = await projectAPI.listTasks(id);
      setTasks(res.data || []);
    } catch (err) { console.log('Failed to load tasks', err); }
  };

  const getChatPhase = () => {
    const map = { req_list: 'requirements', usecase_diagram: 'requirements', rsd: 'rsd', frs: 'requirements', fsd: 'fsd' };
    return map[activePhase] || activePhase;
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await chatAPI.send({ project_id: id, message, phase: getChatPhase() });
      await loadProject();
      await refreshUser();
      setMessage('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const handleAddRequirement = async () => {
    if (!newReqText.trim()) return;
    try {
      await projectAPI.addRequirement(id, { text: newReqText, category: newReqCategory, priority: newReqPriority });
      setNewReqText('');
      await loadProject();
      toast.success('Requirement added');
    } catch { toast.error('Failed to add'); }
  };

  const handleDeleteRequirement = async (reqId) => {
    try { await projectAPI.deleteRequirement(id, reqId); await loadProject(); } catch { toast.error('Failed'); }
  };

  const handleGenerateDoc = async (docType) => {
    setGeneratingDoc(docType);
    try {
      const res = await projectAPI.generateDoc(id, { project_id: id, doc_type: docType });
      await loadProject();
      await refreshUser();
      toast.success(`Generated! ${res.data.tokens_spent} tokens used`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    finally { setGeneratingDoc(null); }
  };

  const handleSaveTechStack = async () => {
    try { await projectAPI.saveTechStack(id, techStack); toast.success('Technology stack saved'); } catch { toast.error('Save failed'); }
  };

  const handleConnectSC = async () => {
    if (!scRepoUrl.trim()) { toast.error('Enter repo URL'); return; }
    try {
      await projectAPI.connectSourceControl(id, { project_id: id, provider: scProvider, repo_url: scRepoUrl, branch: scBranch });
      await loadProject();
      toast.success('Source control connected');
    } catch { toast.error('Connection failed'); }
  };

  const handleDisconnectSC = async () => {
    try { await projectAPI.disconnectSourceControl(id); setScRepoUrl(''); await loadProject(); toast.success('Disconnected'); } catch { toast.error('Failed'); }
  };

  const handleSaveSettings = async () => {
    try {
      await projectAPI.updateSettings(id, settingsForm);
      await loadProject();
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
  };

  const downloadText = (content, filename) => {
    // Convert to Word-compatible HTML format (.docx)
    const docxFilename = filename.replace('.md', '.docx');
    
    // Convert markdown to HTML for Word
    const convertMarkdownToHtml = (md) => {
      return md
        .split('\n')
        .map(line => {
          if (line.startsWith('#### ')) return `<h4>${line.slice(5)}</h4>`;
          if (line.startsWith('### ')) return `<h3>${line.slice(4)}</h3>`;
          if (line.startsWith('## ')) return `<h2>${line.slice(3)}</h2>`;
          if (line.startsWith('# ')) return `<h1>${line.slice(2)}</h1>`;
          if (line.startsWith('- ')) return `<li>${line.slice(2)}</li>`;
          if (line.startsWith('* ')) return `<li>${line.slice(2)}</li>`;
          if (/^\d+\.\s/.test(line)) return `<li>${line.replace(/^\d+\.\s/, '')}</li>`;
          if (line.startsWith('```')) return ''; // Skip code fences
          if (line.startsWith('> ')) return `<blockquote>${line.slice(2)}</blockquote>`;
          if (line.trim() === '') return '<br/>';
          // Handle bold and italic
          let processed = line
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/`(.+?)`/g, '<code>$1</code>');
          return `<p>${processed}</p>`;
        })
        .join('\n');
    };

    const htmlContent = `
<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
<head>
<meta charset="utf-8">
<title>${docxFilename}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }
  h1 { font-size: 24pt; color: #1a1a1a; border-bottom: 2px solid #6366f1; padding-bottom: 8px; }
  h2 { font-size: 18pt; color: #333; margin-top: 24px; }
  h3 { font-size: 14pt; color: #444; margin-top: 18px; }
  h4 { font-size: 12pt; color: #555; margin-top: 12px; }
  p { margin: 8px 0; }
  li { margin: 4px 0; margin-left: 20px; }
  blockquote { border-left: 3px solid #6366f1; padding-left: 12px; margin-left: 0; color: #666; font-style: italic; }
  code { background: #f4f4f4; padding: 2px 6px; border-radius: 3px; font-family: Consolas, monospace; }
  table { border-collapse: collapse; width: 100%; margin: 16px 0; }
  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  th { background: #f8f8f8; }
</style>
</head>
<body>
${convertMarkdownToHtml(content)}
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; 
    a.download = docxFilename; 
    a.click();
    URL.revokeObjectURL(url);
  };

  const phaseChat = project?.chat_history?.filter(m => m.phase === getChatPhase()) || [];
  const requirements = project?.phases?.requirements?.items || [];
  const currentProviderModels = providers.find(p => p.provider_key === settingsForm.llm_provider)?.models || [];

  const getPhaseContent = () => {
    if (activePhase === 'req_list') {
      // Build a markdown preview from the requirements list when no analysis content exists
      const analysisContent = project?.phases?.requirements?.analysis_content;
      if (analysisContent) return analysisContent;
      const items = project?.phases?.requirements?.items || [];
      if (items.length === 0) return '';
      const byCategory = items.reduce((acc, r) => {
        const cat = r.category || 'functional';
        acc[cat] = acc[cat] || [];
        acc[cat].push(r);
        return acc;
      }, {});
      const catLabel = { functional: 'Functional', non_functional: 'Non-Functional', constraint: 'Constraints' };
      let md = `# Requirements\n\nTotal: **${items.length}** requirement${items.length !== 1 ? 's' : ''}\n\n`;
      Object.keys(byCategory).forEach((cat) => {
        md += `## ${catLabel[cat] || cat}\n\n`;
        byCategory[cat].forEach((r, idx) => {
          const prio = r.priority ? ` _( ${r.priority} )_` : '';
          md += `${idx + 1}. ${r.text}${prio}\n`;
        });
        md += `\n`;
      });
      return md;
    }
    if (activePhase === 'usecase_diagram') return project?.phases?.requirements?.usecase_content || '';
    if (activePhase === 'rsd') return project?.phases?.rsd?.content || '';
    if (activePhase === 'frs') return project?.phases?.requirements?.frs_content || '';
    if (activePhase === 'fsd') return project?.phases?.fsd?.content || '';
    if (activePhase === 'executive_summary') return project?.executive_summary || '';
    if (activePhase === 'scope') {
      const sc = project?.project_scope || {};
      const sections = [
        { key: 'in_scope', label: 'In Scope' },
        { key: 'out_of_scope', label: 'Out of Scope' },
        { key: 'assumptions', label: 'Assumptions' },
        { key: 'constraints', label: 'Constraints' },
      ];
      const hasAny = sections.some(s => (sc[s.key] || []).length > 0);
      if (!hasAny) return '';
      let md = `# Project Scope\n\n`;
      sections.forEach(s => {
        const items = sc[s.key] || [];
        md += `## ${s.label}\n\n`;
        if (items.length === 0) {
          md += `_No items yet._\n\n`;
        } else {
          items.forEach(it => { md += `- ${it}\n`; });
          md += `\n`;
        }
      });
      return md;
    }
    return project?.phases?.[activePhase]?.content || '';
  };
  const phaseContent = getPhaseContent();

  if (loading) return (
    <div className="min-h-screen bg-[var(--surface-bg)] flex items-center justify-center">
      <BarShimmerLoader />
    </div>
  );

  // Determine if preview should be shown for this phase
  const showPreviewForPhase = !['tasks', 'bugs', 'roadmap', 'members', 'jira', 'source_control', 'project_settings', 'change_requests', 'showstoppers', 'release_notes', 'test_cases', 'new_features', 'code', 'sprints', 'meetings', 'executive_summary', 'scope'].includes(activePhase);
  const actualShowPreview = showPreview && showPreviewForPhase;

  // RBAC: Check if user can access a phase
  const canAccessPhase = (phaseId) => {
    if (userRole === 'owner' || userRole === 'admin') return true;
    const permissions = rolePermissions[userRole] || [];
    if (permissions.includes('all')) return true;
    // Viewers can see all but not interact (handled separately)
    if (permissions.includes('view_only')) return true;
    const requiredPermission = phaseToPermission[phaseId] || phaseId;
    return permissions.includes(requiredPermission);
  };

  // Check if user can edit (not just view)
  const canEditPhase = (phaseId) => {
    if (userRole === 'viewer') return false;
    return canAccessPhase(phaseId);
  };

  const isOwner = userRole === 'owner';
  const isViewer = userRole === 'viewer';

  return (
    <div className="h-screen bg-[var(--surface-bg)] flex flex-col" data-testid="project-workspace">
      {/* Top Bar */}
      <nav className="shrink-0 backdrop-blur-xl bg-[var(--nav-bg)] border-b border-[var(--surface-border)]">
        <div className="px-4 h-11 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate('/dashboard')}><ArrowLeft className="w-4 h-4" /></Button>
            <Terminal className="w-4 h-4 text-[var(--accent-indigo)]" />
            <span className="text-sm font-semibold truncate max-w-[200px]" data-testid="project-name">{project?.name}</span>
            <Badge variant="outline" className="text-[10px]">{project?.project_type}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {project?.source_control && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]"><GitBranch className="w-3 h-3 mr-1" /> {project.source_control.provider}</Badge>}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowPreview(!showPreview)} data-testid="toggle-preview">
              {showPreview ? <PanelRight className="w-4 h-4" /> : <PanelLeft className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <div className="flex items-center gap-1.5 bg-[var(--surface-card)] rounded-md px-2.5 py-1 border border-[var(--surface-border)]">
              <Coins className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-medium" data-testid="workspace-token-balance">{user?.token_balance}</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-48 shrink-0 border-r border-[var(--surface-border)] bg-[var(--surface-card)] flex flex-col overflow-y-auto">
          <div className="p-2 space-y-3">
            {/* Role Indicator */}
            {userRole !== 'owner' && (
              <div className={`mx-2 p-2 rounded-lg border ${isViewer ? 'bg-amber-500/5 border-amber-500/20' : 'bg-[var(--accent-indigo)]/5 border-[var(--accent-indigo)]/20'}`}>
                <div className="flex items-center gap-1.5">
                  <Shield className={`w-3 h-3 ${isViewer ? 'text-amber-400' : 'text-[var(--accent-indigo)]'}`} />
                  <span className={`text-[10px] font-medium capitalize ${isViewer ? 'text-amber-400' : 'text-[var(--accent-indigo)]'}`}>{userRole}</span>
                </div>
                <p className="text-[9px] opacity-50 mt-0.5">{isViewer ? 'Read-only access' : 'Limited access'}</p>
              </div>
            )}

            {/* SDLC Phases */}
            <div>
              <p className="text-[9px] font-medium tracking-widest uppercase opacity-40 mb-1.5 px-2">SDLC Phases</p>
              {mainPhases.map((phase) => {
                const hasAccess = canAccessPhase(phase.id);
                return (
                <div key={phase.id}>
                  {phase.expandable ? (
                    <>
                      <button
                        onClick={() => {
                          if (phase.id === 'project_specs_group') setProjectSpecsExpanded(!projectSpecsExpanded);
                          else setReqGroupExpanded(!reqGroupExpanded);
                        }}
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors opacity-80 hover:opacity-100 font-semibold"
                        data-testid={`phase-${phase.id}`}
                      >
                        {(phase.id === 'project_specs_group' ? projectSpecsExpanded : reqGroupExpanded)
                          ? <ChevronDown className="w-3 h-3" />
                          : <ChevronRight className="w-3 h-3" />}
                        <phase.icon className="w-3.5 h-3.5" /><span className="text-left flex-1">{phase.label}</span>
                      </button>
                      {(phase.id === 'project_specs_group' ? projectSpecsExpanded : reqGroupExpanded) && (
                        <div className="ml-3 border-l border-[var(--surface-border)] pl-1.5 space-y-0.5 mb-1">
                          {phase.subPages.map((sub) => {
                            // Nested expandable (e.g. Requirements inside Project Specs)
                            if (sub.expandable) {
                              return (
                                <div key={sub.id}>
                                  <button
                                    onClick={() => setReqGroupExpanded(!reqGroupExpanded)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] transition-colors opacity-70 hover:opacity-100 font-medium"
                                    data-testid={`phase-${sub.id}`}
                                  >
                                    {reqGroupExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    <sub.icon className="w-3 h-3 shrink-0" />
                                    <span className="truncate flex-1 text-left">{sub.label}</span>
                                  </button>
                                  {reqGroupExpanded && (
                                    <div className="ml-3 border-l border-[var(--surface-border)] pl-1.5 space-y-0.5 mb-1">
                                      {sub.subPages.map((leaf) => {
                                        const leafHasAccess = canAccessPhase(leaf.id);
                                        return (
                                          <button
                                            key={leaf.id}
                                            onClick={() => leafHasAccess && setActivePhase(leaf.id)}
                                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] transition-colors text-left ${!leafHasAccess ? 'opacity-30 cursor-not-allowed' : activePhase === leaf.id ? 'bg-[var(--accent-indigo)]/15 text-[var(--accent-indigo)]' : 'opacity-60 hover:opacity-90'}`}
                                            data-testid={`phase-${leaf.id}`}
                                            disabled={!leafHasAccess}
                                          >
                                            <leaf.icon className="w-3 h-3 shrink-0" />
                                            <span className="truncate flex-1 text-left">{leaf.label}</span>
                                            {!leafHasAccess && <Lock className="w-3 h-3 opacity-50" />}
                                            {leafHasAccess && leaf.id !== 'req_list' && getDocContent(project, leaf.id) && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            const subHasAccess = canAccessPhase(sub.id);
                            return (
                              <button
                                key={sub.id}
                                onClick={() => subHasAccess && setActivePhase(sub.id)}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] transition-colors text-left ${!subHasAccess ? 'opacity-30 cursor-not-allowed' : activePhase === sub.id ? 'bg-[var(--accent-indigo)]/15 text-[var(--accent-indigo)]' : 'opacity-60 hover:opacity-90'}`}
                                data-testid={`phase-${sub.id}`}
                                disabled={!subHasAccess}
                              >
                                <sub.icon className="w-3 h-3 shrink-0" />
                                <span className="truncate flex-1 text-left">{sub.label}</span>
                                {!subHasAccess && <Lock className="w-3 h-3 opacity-50" />}
                                {subHasAccess && project?.phases?.[sub.id]?.status === 'in_progress' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <button onClick={() => hasAccess && setActivePhase(phase.id)}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left ${!hasAccess ? 'opacity-30 cursor-not-allowed' : activePhase === phase.id ? 'bg-[var(--accent-indigo)]/15 text-[var(--accent-indigo)]' : 'opacity-50 hover:opacity-80'}`}
                      data-testid={`phase-${phase.id}`}
                      disabled={!hasAccess}
                    >
                      <phase.icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate flex-1 text-left">{phase.label}</span>
                      {!hasAccess && <Lock className="w-3 h-3 opacity-50" />}
                      {hasAccess && project?.phases?.[phase.id]?.status === 'in_progress' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />}
                    </button>
                  )}
                </div>
              )})}
            </div>

            {/* Management */}
            <div>
              <p className="text-[9px] font-medium tracking-widest uppercase opacity-40 mb-1.5 px-2">Management</p>
              {managementPhases.map((phase) => {
                const hasAccess = canAccessPhase(phase.id);
                return (
                <button key={phase.id} onClick={() => hasAccess && setActivePhase(phase.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left ${!hasAccess ? 'opacity-30 cursor-not-allowed' : activePhase === phase.id ? 'bg-[var(--accent-indigo)]/15 text-[var(--accent-indigo)]' : 'opacity-50 hover:opacity-80'}`}
                  data-testid={`phase-${phase.id}`}
                  disabled={!hasAccess}
                >
                  <phase.icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate flex-1 text-left">{phase.label}</span>
                  {!hasAccess && <Lock className="w-3 h-3 opacity-50" />}
                  {hasAccess && phase.id === 'bugs' && tasks.filter(t => t.task_type === 'bug' && t.status !== 'done').length > 0 && (
                    <Badge variant="outline" className="text-[8px] h-4 text-red-400 border-red-500/20">{tasks.filter(t => t.task_type === 'bug' && t.status !== 'done').length}</Badge>
                  )}
                </button>
              )})}
            </div>

            {/* Settings */}
            <div>
              <p className="text-[9px] font-medium tracking-widest uppercase opacity-40 mb-1.5 px-2">Settings</p>
              {settingsPhases.map((phase) => (
                <button key={phase.id} onClick={() => setActivePhase(phase.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors ${activePhase === phase.id ? 'bg-[var(--accent-indigo)]/15 text-[var(--accent-indigo)]' : 'opacity-50 hover:opacity-80'}`}
                  data-testid={`phase-${phase.id}`}
                >
                  <phase.icon className="w-3.5 h-3.5" /><span className="truncate">{phase.label}</span>
                  {phase.id === 'source_control' && project?.source_control && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 ml-auto shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          <div className={`flex flex-col overflow-hidden ${actualShowPreview ? 'w-1/2' : 'flex-1'} ${actualShowPreview ? 'border-r border-[var(--surface-border)]' : ''}`}>

            {/* REQUIREMENTS LIST */}
            {activePhase === 'req_list' && <RequirementsListPanel requirements={requirements} newReqText={newReqText} setNewReqText={setNewReqText} newReqCategory={newReqCategory} setNewReqCategory={setNewReqCategory} newReqPriority={newReqPriority} setNewReqPriority={setNewReqPriority} handleAddRequirement={handleAddRequirement} handleDeleteRequirement={handleDeleteRequirement} handleGenerateDoc={handleGenerateDoc} generatingDoc={generatingDoc} projectName={project?.name} downloadText={downloadText} projectId={id} onRequirementsGenerated={loadProject} />}

            {/* DOCUMENT PHASES */}
            {['usecase_diagram', 'rsd', 'frs', 'fsd'].includes(activePhase) && (
              <DocumentPhasePanel activePhase={activePhase} phaseContent={phaseContent} handleGenerateDoc={handleGenerateDoc} generatingDoc={generatingDoc} requirements={requirements} projectName={project?.name} downloadText={downloadText} />
            )}

            {/* TECHNOLOGY */}
            {activePhase === 'technology' && <TechnologyPanel techStack={techStack} setTechStack={setTechStack} handleSaveTechStack={handleSaveTechStack} />}

            {/* SOURCE CONTROL - Full width */}
            {activePhase === 'source_control' && <SourceControlPanel scProvider={scProvider} setScProvider={setScProvider} scRepoUrl={scRepoUrl} setScRepoUrl={setScRepoUrl} scBranch={scBranch} setScBranch={setScBranch} handleConnectSC={handleConnectSC} handleDisconnectSC={handleDisconnectSC} sourceControl={project?.source_control} />}

            {/* PROJECT SETTINGS - Full width */}
            {activePhase === 'project_settings' && <ProjectSettingsPanel settingsForm={settingsForm} setSettingsForm={setSettingsForm} providers={providers} currentProviderModels={currentProviderModels} handleSaveSettings={handleSaveSettings} />}

            {/* TASKS PANEL - Full width */}
            {activePhase === 'tasks' && <TasksPanel projectId={id} members={members} epics={epics} onRefresh={loadProject} />}

            {/* BUG TRACKER PANEL - Full width */}
            {activePhase === 'bugs' && <BugTrackerPanel projectId={id} members={members} />}

            {/* ROADMAP PANEL - Full width */}
            {activePhase === 'roadmap' && <RoadmapPanel projectId={id} tasks={tasks} />}

            {/* SPRINTS PANEL - Full width */}
            {activePhase === 'sprints' && <SprintsPanel projectId={id} tasks={tasks} onTasksChange={loadTasks} />}

            {/* MEETINGS PANEL - Full width */}
            {activePhase === 'meetings' && <MeetingsPanel projectId={id} canEdit={canEditPhase('meetings')} />}

            {/* MEMBERS PANEL - Full width */}
            {activePhase === 'members' && <MembersPanel projectId={id} currentUserId={user?.id} isOwner={isOwner} onMembersChange={setMembers} />}

            {/* JIRA PANEL - Full width */}
            {activePhase === 'jira' && <JiraPanel projectId={id} isOwner={isOwner} />}

            {/* CHANGE REQUESTS PANEL - Full width */}
            {activePhase === 'change_requests' && <ChangeRequestsPanel projectId={id} canEdit={canEditPhase('change_requests')} />}

            {/* SHOWSTOPPERS PANEL - Full width */}
            {activePhase === 'showstoppers' && <ShowstoppersPanel projectId={id} canEdit={canEditPhase('showstoppers')} />}

            {/* RELEASE NOTES PANEL - Full width */}
            {activePhase === 'release_notes' && <ReleaseNotesPanel projectId={id} canEdit={canEditPhase('release_notes')} />}

            {/* TEST CASES PANEL - Full width */}
            {activePhase === 'test_cases' && <TestCasesPanel projectId={id} canEdit={canEditPhase('test_cases')} />}

            {/* NEW FEATURES PANEL - Full width */}
            {activePhase === 'new_features' && <NewFeaturesPanel projectId={id} canEdit={canEditPhase('new_features')} />}

            {/* EXECUTIVE SUMMARY - Full width */}
            {activePhase === 'executive_summary' && <ExecutiveSummaryPanel projectId={id} project={project} onRefresh={loadProject} canEdit={canEditPhase('executive_summary')} />}

            {/* PROJECT SCOPE - Full width */}
            {activePhase === 'scope' && <ProjectScopePanel projectId={id} project={project} onRefresh={loadProject} canEdit={canEditPhase('scope')} />}

            {/* CODE PANEL - Full width with file tree */}
            {activePhase === 'code' && <CodePanel projectId={id} project={project} phaseChat={phaseChat} sending={sending} onSendMessage={async (msg) => {
              setSending(true);
              try {
                await chatAPI.send({ project_id: id, message: msg, phase: 'code' });
                await loadProject();
                await refreshUser();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to send');
              } finally {
                setSending(false);
              }
            }} />}

            {/* MODERN CHAT PHASES */}
            {['design', 'domain', 'tests'].includes(activePhase) && (
              <ModernChat
                messages={phaseChat}
                isProcessing={sending}
                placeholder={`Ask about ${activePhase}...`}
                emptyStateTitle={`${activePhase.charAt(0).toUpperCase() + activePhase.slice(1)} Phase`}
                emptyStateDescription={`Start a conversation to get help with your ${activePhase} phase.`}
                onSendMessage={async (msg) => {
                  setSending(true);
                  try {
                    await chatAPI.send({ project_id: id, message: msg, phase: getChatPhase() });
                    await loadProject();
                    await refreshUser();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to send');
                  } finally {
                    setSending(false);
                  }
                }}
              />
            )}

            {/* ARCHITECTURE PHASE - With Diagram Generation */}
            {activePhase === 'architecture' && (
              <ArchitecturePanel 
                projectId={id}
                project={project}
                phaseChat={phaseChat}
                sending={sending}
                onSendMessage={async (msg) => {
                  setSending(true);
                  try {
                    await chatAPI.send({ project_id: id, message: msg, phase: 'architecture' });
                    await loadProject();
                    await refreshUser();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to send');
                  } finally {
                    setSending(false);
                  }
                }}
                onRefresh={loadProject}
              />
            )}
          </div>

          {/* Right Preview Panel - Only show for phases that need it */}
          {actualShowPreview && (
            <div className="w-1/2 flex flex-col overflow-hidden" data-testid="preview-panel">
              <div className="border-b border-[var(--surface-border)] px-3 py-1.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-[var(--accent-indigo)]" />
                  <span className="text-xs font-medium">{activePhase === 'req_list' ? 'Requirements Preview' : activePhase === 'technology' ? 'Stack Preview' : activePhase === 'executive_summary' ? 'Executive Summary Preview' : activePhase === 'scope' ? 'Scope Preview' : activePhase === 'rsd' ? 'RSD Document' : activePhase === 'frs' ? 'FRS Document' : activePhase === 'fsd' ? 'FSD Document' : 'Preview'}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2" data-testid="save-code-btn" onClick={() => toast.success('Saved')}><Save className="w-3 h-3 mr-1" /> Save</Button>
                  <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2" data-testid="push-github-btn"><GitBranch className="w-3 h-3 mr-1" /> GitHub</Button>
                </div>
              </div>
              {activePhase === 'req_list' ? (
                <RequirementsAnalysisPanel requirements={requirements} analysisContent={project?.phases?.requirements?.analysis_content} questionnaireContent={project?.phases?.requirements?.questionnaire_content} />
              ) : activePhase === 'technology' ? (
                <TechStackPreview techStack={techStack} />
              ) : (
                <CodePreview content={phaseContent} phase={activePhase} project={project} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper to check if a doc sub-page has content
function getDocContent(project, subId) {
  if (!project) return '';
  if (subId === 'usecase_diagram') return project.phases?.requirements?.usecase_content;
  if (subId === 'rsd') return project.phases?.rsd?.content;
  if (subId === 'frs') return project.phases?.requirements?.frs_content;
  if (subId === 'fsd') return project.phases?.fsd?.content;
  return '';
}

/* ====== SUB-PANELS ====== */

function RequirementsListPanel({ requirements, newReqText, setNewReqText, newReqCategory, setNewReqCategory, newReqPriority, setNewReqPriority, handleAddRequirement, handleDeleteRequirement, handleGenerateDoc, generatingDoc, projectName, downloadText, projectId, onRequirementsGenerated }) {
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerateRequirements = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please describe what you want to build');
      return;
    }
    setGenerating(true);
    try {
      const res = await projectAPI.generateRequirements(projectId, { prompt: aiPrompt });
      toast.success(`Generated ${res.data.requirements?.length || 0} requirements`);
      setShowAIModal(false);
      setAiPrompt('');
      if (onRequirementsGenerated) onRequirementsGenerated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate requirements');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-xs"><ClipboardList className="w-4 h-4 mr-1" /> Requirements List</Badge>
        <Button size="sm" variant="default" className="text-sm h-8 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white" onClick={() => setShowAIModal(true)} data-testid="gen-ai-req-btn">
          <Bot className="w-4 h-4 mr-1" /> Generate with AI
        </Button>
        <Button size="sm" variant="outline" className="text-sm h-8" onClick={() => handleGenerateDoc('questionnaire')} disabled={!!generatingDoc} data-testid="gen-questionnaire-btn">
          {generatingDoc === 'questionnaire' ? <DotPulseLoader text="" className="mr-1" /> : <ListChecks className="w-4 h-4 mr-1" />} Questionnaire
        </Button>
        <Button size="sm" variant="outline" className="text-sm h-8" onClick={() => handleGenerateDoc('analyze_requirements')} disabled={!!generatingDoc || requirements.length === 0} data-testid="analyze-req-btn">
          {generatingDoc === 'analyze_requirements' ? <DotPulseLoader text="" className="mr-1" /> : <Network className="w-4 h-4 mr-1" />} Analyze
        </Button>
        <Button size="sm" variant="outline" className="text-sm h-8" onClick={() => handleGenerateDoc('generate_tests')} disabled={!!generatingDoc || requirements.length === 0} data-testid="gen-tests-btn">
          {generatingDoc === 'generate_tests' ? <DotPulseLoader text="" className="mr-1" /> : <TestTube className="w-4 h-4 mr-1" />} Generate Tests
        </Button>
        {requirements.length > 0 && (
          <Button size="sm" variant="ghost" className="text-sm h-8 ml-auto" onClick={() => {
            const content = requirements.map((r, i) => `${i + 1}. [${r.category}][${r.priority}]${r.is_ai_generated ? '[AI]' : ''} ${r.text}`).join('\n');
            downloadText(content, `requirements_${projectName}.md`);
          }} data-testid="download-req-btn"><Download className="w-4 h-4 mr-1" /> Download</Button>
        )}
      </div>

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !generating && setShowAIModal(false)}>
          <div className="bg-[var(--surface-bg)] border border-[var(--surface-border)] rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()} data-testid="ai-gen-modal">
            <div className="px-5 py-4 border-b border-[var(--surface-border)] flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Generate Requirements with AI</h3>
                <p className="text-sm opacity-50">Describe your project and we'll generate requirements</p>
              </div>
              <Button variant="ghost" size="icon" className="ml-auto h-8 w-8" onClick={() => setShowAIModal(false)} disabled={generating}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <Label className="text-sm mb-2 block">What would you like to build?</Label>
                <Textarea 
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., A task management app with user authentication, project boards, task assignment, due dates, notifications, and team collaboration features..."
                  className="min-h-[120px] text-sm bg-transparent border-[var(--surface-border)]"
                  disabled={generating}
                  data-testid="ai-prompt-input"
                />
              </div>
              <div className="bg-[var(--surface-card)] rounded-lg p-3 border border-[var(--surface-border)]">
                <p className="text-xs opacity-50 mb-2">AI will generate requirements including:</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">Functional</Badge>
                  <Badge variant="outline" className="text-xs">Non-Functional</Badge>
                  <Badge variant="outline" className="text-xs">Constraints</Badge>
                  <Badge variant="outline" className="text-xs">Security</Badge>
                  <Badge variant="outline" className="text-xs">Performance</Badge>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[var(--surface-border)] flex justify-end gap-2">
              <Button variant="ghost" className="text-sm h-9" onClick={() => setShowAIModal(false)} disabled={generating}>
                Cancel
              </Button>
              <Button 
                className="text-sm h-9 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white min-w-[140px]" 
                onClick={handleGenerateRequirements}
                disabled={generating || !aiPrompt.trim()}
                data-testid="submit-ai-gen"
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 mr-1" /> Generate
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-4 space-y-3" data-testid="add-req-form">
            <Textarea value={newReqText} onChange={(e) => setNewReqText(e.target.value)} placeholder="Enter requirement..." className="bg-transparent border-[var(--surface-border)] text-sm min-h-[60px]" data-testid="req-text-input" />
            <div className="flex gap-2 items-center">
              <Select value={newReqCategory} onValueChange={setNewReqCategory}>
                <SelectTrigger className="w-40 h-8 text-sm bg-transparent border-[var(--surface-border)]" data-testid="req-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="functional" className="text-sm">Functional</SelectItem>
                  <SelectItem value="non_functional" className="text-sm">Non-Functional</SelectItem>
                  <SelectItem value="constraint" className="text-sm">Constraint</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newReqPriority} onValueChange={setNewReqPriority}>
                <SelectTrigger className="w-32 h-8 text-sm bg-transparent border-[var(--surface-border)]" data-testid="req-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="high" className="text-sm">High</SelectItem>
                  <SelectItem value="medium" className="text-sm">Medium</SelectItem>
                  <SelectItem value="low" className="text-sm">Low</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="h-8 text-sm bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white ml-auto" onClick={handleAddRequirement} data-testid="add-req-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          </div>
          {requirements.map((req, i) => (
            <div key={req.id} className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-3 flex items-start gap-3" data-testid={`req-item-${i}`}>
              <div className="w-6 h-6 rounded bg-[var(--accent-indigo)]/10 flex items-center justify-center shrink-0 mt-0.5"><span className="text-xs font-bold text-[var(--accent-indigo)]">{i + 1}</span></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-relaxed">{req.text}</p>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Badge variant="outline" className="text-xs">{req.category}</Badge>
                  <Badge className={`text-xs ${req.priority === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/20' : req.priority === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>{req.priority}</Badge>
                  {req.is_ai_generated && (
                    <Badge className="text-xs bg-gradient-to-r from-purple-500/10 to-indigo-500/10 text-purple-400 border-purple-500/20">
                      <Bot className="w-3 h-3 mr-1" /> AI Generated
                    </Badge>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-30 hover:opacity-100 hover:text-red-400" onClick={() => handleDeleteRequirement(req.id)} data-testid={`delete-req-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
          {requirements.length === 0 && <p className="text-center opacity-30 py-10 text-sm">Add requirements above or generate with AI. They drive RSD, FSD, and test generation.</p>}
        </div>
      </ScrollArea>
    </div>
  );
}

function DocumentPhasePanel({ activePhase, phaseContent, handleGenerateDoc, generatingDoc, requirements, projectName, downloadText }) {
  const labels = { usecase_diagram: 'Use Case Diagram', rsd: 'RSD', frs: 'FRS', fsd: 'FSD' };
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-[10px]">{labels[activePhase]}</Badge>
        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleGenerateDoc(activePhase)} disabled={!!generatingDoc} data-testid={`gen-${activePhase}-btn`}>
          {generatingDoc === activePhase ? <DotPulseLoader text="" className="mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
          {phaseContent ? 'Regenerate' : 'Generate'} {labels[activePhase]}
        </Button>
        {phaseContent && (
          <Button size="sm" variant="ghost" className="text-xs h-7 ml-auto" onClick={() => downloadText(phaseContent, `${activePhase}_${projectName}.md`)} data-testid="download-doc-btn"><Download className="w-3 h-3 mr-1" /> Download</Button>
        )}
      </div>
      {generatingDoc === activePhase ? (
        <div className="flex-1 flex items-center justify-center"><DotPulseLoader text="Generating document..." /></div>
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-4">
            {phaseContent ? <MermaidContent content={phaseContent} /> : (
              <div className="text-center py-16"><FileText className="w-10 h-10 opacity-10 mx-auto mb-2" /><p className="opacity-30 text-sm">Click "Generate" to create {labels[activePhase]}</p><p className="opacity-20 text-xs mt-1">{requirements.length > 0 ? `Based on ${requirements.length} requirements` : 'Add requirements first'}</p></div>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

function TechnologyPanel({ techStack, setTechStack, handleSaveTechStack }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--surface-border)] flex items-center gap-2"><Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-[10px]"><Terminal className="w-3 h-3 mr-1" /> Technology Stack</Badge></div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-lg">
          {[{ key: 'frontend', label: 'Frontend Framework' }, { key: 'backend', label: 'Backend Framework' }, { key: 'database', label: 'Database' }, { key: 'cloud', label: 'Cloud / Hosting' }, { key: 'devops', label: 'DevOps / CI/CD' }].map(({ key, label }) => (
            <div key={key} data-testid={`tech-${key}`}>
              <Label className="text-xs font-medium opacity-60 mb-1.5 block">{label}</Label>
              <Select value={techStack[key]} onValueChange={(v) => setTechStack(prev => ({ ...prev, [key]: v }))}>
                <SelectTrigger className="bg-transparent border-[var(--surface-border)] h-9 text-sm" data-testid={`tech-${key}-select`}><SelectValue placeholder={`Select ${label}`} /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">{techOptions[key].map(o => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
          <div><Label className="text-xs font-medium opacity-60 mb-1.5 block">Other Technologies</Label><Input value={techStack.other || ''} onChange={(e) => setTechStack(prev => ({ ...prev, other: e.target.value }))} className="bg-transparent border-[var(--surface-border)] h-9 text-sm" placeholder="GraphQL, Stripe, Redis..." data-testid="tech-other-input" /></div>
          <Button className="bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white text-sm h-9" onClick={handleSaveTechStack} data-testid="save-tech-btn"><Save className="w-4 h-4 mr-1.5" /> Save Technology Stack</Button>
        </div>
      </ScrollArea>
    </div>
  );
}

function SourceControlPanel({ scProvider, setScProvider, scRepoUrl, setScRepoUrl, scBranch, setScBranch, handleConnectSC, handleDisconnectSC, sourceControl }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--surface-border)] flex items-center gap-2">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-[10px]"><FolderGit2 className="w-3 h-3 mr-1" /> Source Control</Badge>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-5 max-w-lg">
          {sourceControl?.status === 'connected' ? (
            <div className="space-y-4">
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4" data-testid="sc-connected">
                <div className="flex items-center gap-2 mb-2"><GitBranch className="w-4 h-4 text-emerald-400" /><span className="text-sm font-medium text-emerald-400">Connected</span></div>
                <p className="text-xs opacity-60 mb-1"><strong>Provider:</strong> {sourceControl.provider === 'github' ? 'GitHub' : 'Azure DevOps'}</p>
                <p className="text-xs opacity-60 mb-1"><strong>Repo:</strong> {sourceControl.repo_url}</p>
                <p className="text-xs opacity-60"><strong>Branch:</strong> {sourceControl.branch}</p>
              </div>
              <Button variant="outline" size="sm" className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={handleDisconnectSC} data-testid="disconnect-sc-btn"><Unplug className="w-3 h-3 mr-1" /> Disconnect</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium opacity-60 mb-1.5 block">Provider</Label>
                <Select value={scProvider} onValueChange={setScProvider}>
                  <SelectTrigger className="bg-transparent border-[var(--surface-border)] h-9 text-sm" data-testid="sc-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                    <SelectItem value="github" className="text-sm"><div className="flex items-center gap-2"><GitBranch className="w-3.5 h-3.5" /> GitHub</div></SelectItem>
                    <SelectItem value="azure_devops" className="text-sm"><div className="flex items-center gap-2"><FolderGit2 className="w-3.5 h-3.5" /> Azure DevOps</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium opacity-60 mb-1.5 block">Repository URL</Label>
                <Input value={scRepoUrl} onChange={(e) => setScRepoUrl(e.target.value)} className="bg-transparent border-[var(--surface-border)] h-9 text-sm" placeholder={scProvider === 'github' ? 'https://github.com/user/repo' : 'https://dev.azure.com/org/project/_git/repo'} data-testid="sc-repo-input" />
              </div>
              <div>
                <Label className="text-xs font-medium opacity-60 mb-1.5 block">Branch</Label>
                <Input value={scBranch} onChange={(e) => setScBranch(e.target.value)} className="bg-transparent border-[var(--surface-border)] h-9 text-sm" placeholder="main" data-testid="sc-branch-input" />
              </div>
              <Button className="bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white text-sm h-9" onClick={handleConnectSC} data-testid="connect-sc-btn"><GitBranch className="w-4 h-4 mr-1.5" /> Connect Repository</Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function ProjectSettingsPanel({ settingsForm, setSettingsForm, providers, currentProviderModels, handleSaveSettings }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--surface-border)] flex items-center gap-2">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-[10px]"><Settings className="w-3 h-3 mr-1" /> Project Settings</Badge>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-5 max-w-lg">
          <div data-testid="settings-name"><Label className="text-xs font-medium opacity-60 mb-1.5 block">Project Name</Label><Input value={settingsForm.name} onChange={(e) => setSettingsForm(f => ({ ...f, name: e.target.value }))} className="bg-transparent border-[var(--surface-border)] h-9 text-sm" data-testid="settings-name-input" /></div>
          <div><Label className="text-xs font-medium opacity-60 mb-1.5 block">Description</Label><Textarea value={settingsForm.description} onChange={(e) => setSettingsForm(f => ({ ...f, description: e.target.value }))} className="bg-transparent border-[var(--surface-border)] text-sm min-h-[60px]" data-testid="settings-desc-input" /></div>
          <div className="border-t border-[var(--surface-border)] pt-4">
            <p className="text-xs font-medium opacity-40 tracking-wider uppercase mb-3">LLM Configuration</p>
            <div className="space-y-4">
              <div><Label className="text-xs font-medium opacity-60 mb-1.5 block">Provider</Label>
                <Select value={settingsForm.llm_provider} onValueChange={(v) => setSettingsForm(f => ({ ...f, llm_provider: v, llm_model: '' }))}>
                  <SelectTrigger className="bg-transparent border-[var(--surface-border)] h-9 text-sm" data-testid="settings-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">{providers.map(p => <SelectItem key={p.provider_key} value={p.provider_key} className="text-sm">{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-medium opacity-60 mb-1.5 block">Model</Label>
                <Select value={settingsForm.llm_model} onValueChange={(v) => setSettingsForm(f => ({ ...f, llm_model: v }))}>
                  <SelectTrigger className="bg-transparent border-[var(--surface-border)] h-9 text-sm" data-testid="settings-model-select"><SelectValue placeholder="Select model" /></SelectTrigger>
                  <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">{currentProviderModels.map(m => <SelectItem key={m} value={m} className="text-sm">{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <Button className="bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white text-sm h-9" onClick={handleSaveSettings} data-testid="save-settings-btn"><Save className="w-4 h-4 mr-1.5" /> Save Settings</Button>
        </div>
      </ScrollArea>
    </div>
  );
}

function ChatPhasePanel({ activePhase, phaseChat, phaseContent, sending, chatEndRef, projectName, downloadText }) {
  const labels = { design: 'Design', architecture: 'Architecture', domain: 'Domain Model', code: 'Code', tests: 'Testing' };
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[var(--surface-border)] flex items-center gap-2">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-[10px]">{labels[activePhase]}</Badge>
        {phaseContent && <Button size="sm" variant="ghost" className="text-xs h-7 ml-auto" onClick={() => downloadText(phaseContent, `${activePhase}_${projectName}.md`)} data-testid="download-phase-btn"><Download className="w-3 h-3 mr-1" /> Download</Button>}
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {phaseChat.length === 0 && !phaseContent && (
            <div className="text-center py-10"><Bot className="w-10 h-10 text-[var(--accent-indigo)] opacity-20 mx-auto mb-2" /><p className="opacity-40 text-sm">Start the {labels[activePhase]} phase</p><p className="opacity-20 text-xs mt-1">Use the chat below to generate content</p></div>
          )}
          {phaseContent && !phaseChat.length && <MermaidContent content={phaseContent} />}
          {phaseChat.map((msg, i) => (
            <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`chat-msg-${i}`}>
              {msg.role === 'assistant' && <div className="w-6 h-6 rounded-md bg-[var(--accent-indigo)]/10 flex items-center justify-center shrink-0 mt-0.5"><Bot className="w-3.5 h-3.5 text-[var(--accent-indigo)]" /></div>}
              <div className={`max-w-[85%] rounded-lg p-2.5 text-xs ${msg.role === 'user' ? 'bg-[var(--accent-indigo)]/15' : 'bg-[var(--surface-card)] border border-[var(--surface-border)]'}`}><MermaidContent content={msg.content} /></div>
              {msg.role === 'user' && <div className="w-6 h-6 rounded-md bg-[var(--surface-card)] border border-[var(--surface-border)] flex items-center justify-center shrink-0 mt-0.5"><User className="w-3.5 h-3.5 opacity-60" /></div>}
            </div>
          ))}
          {sending && <ProcessingIndicator />}
          <div ref={chatEndRef} />
        </div>
      </ScrollArea>
    </div>
  );
}

function RequirementsAnalysisPanel({ requirements, analysisContent, questionnaireContent }) {
  const [tab, setTab] = useState('list');

  // Build a clean markdown preview of the requirements
  const requirementsMd = (() => {
    if (!requirements || requirements.length === 0) return '';
    const byCategory = requirements.reduce((acc, r) => {
      const cat = r.category || 'functional';
      acc[cat] = acc[cat] || [];
      acc[cat].push(r);
      return acc;
    }, {});
    const catLabel = { functional: 'Functional', non_functional: 'Non-Functional', constraint: 'Constraints' };
    let md = `# Requirements\n\nTotal: **${requirements.length}** requirement${requirements.length !== 1 ? 's' : ''}\n\n`;
    Object.keys(byCategory).forEach((cat) => {
      md += `## ${catLabel[cat] || cat}\n\n`;
      byCategory[cat].forEach((r, idx) => {
        const prio = r.priority ? ` _( ${r.priority} )_` : '';
        md += `${idx + 1}. ${r.text}${prio}\n`;
      });
      md += `\n`;
    });
    return md;
  })();

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-3 py-1 border-b border-[var(--surface-border)] flex gap-1 flex-wrap">
        <button onClick={() => setTab('list')} className={`text-[10px] px-2 py-1 rounded ${tab === 'list' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="list-tab"><ListChecks className="w-3 h-3 inline mr-1" />Requirements</button>
        <button onClick={() => setTab('analysis')} className={`text-[10px] px-2 py-1 rounded ${tab === 'analysis' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="analysis-tab"><Network className="w-3 h-3 inline mr-1" />Relations</button>
        <button onClick={() => setTab('questionnaire')} className={`text-[10px] px-2 py-1 rounded ${tab === 'questionnaire' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="questionnaire-tab"><ListChecks className="w-3 h-3 inline mr-1" />Questionnaire</button>
        <button onClick={() => setTab('summary')} className={`text-[10px] px-2 py-1 rounded ${tab === 'summary' ? 'bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)]' : 'opacity-40 hover:opacity-60'}`} data-testid="summary-tab"><BarChart3 className="w-3 h-3 inline mr-1" />Summary</button>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4">
          {tab === 'list' && (
            requirementsMd
              ? <MarkdownRenderer content={requirementsMd} />
              : <div className="text-center py-16" data-testid="requirements-empty"><ListChecks className="w-10 h-10 opacity-10 mx-auto mb-2" /><p className="opacity-30 text-sm">No requirements yet</p><p className="text-xs opacity-20 mt-1">Add or generate requirements to see them here</p></div>
          )}
          {tab === 'analysis' && (analysisContent ? <MermaidContent content={analysisContent} /> : <div className="text-center py-16"><Network className="w-10 h-10 opacity-10 mx-auto mb-2" /><p className="opacity-30 text-sm">Click "Analyze" to see relationships</p></div>)}
          {tab === 'questionnaire' && (questionnaireContent ? <MermaidContent content={questionnaireContent} /> : <div className="text-center py-16"><ListChecks className="w-10 h-10 opacity-10 mx-auto mb-2" /><p className="opacity-30 text-sm">Generate a questionnaire</p></div>)}
          {tab === 'summary' && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold opacity-80">Requirements Summary</h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-2 text-center"><p className="text-lg font-bold text-[var(--accent-indigo)]">{requirements.length}</p><p className="text-[9px] opacity-40">Total</p></div>
                <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-2 text-center"><p className="text-lg font-bold text-red-400">{requirements.filter(r => r.priority === 'high').length}</p><p className="text-[9px] opacity-40">High</p></div>
                <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-2 text-center"><p className="text-lg font-bold text-amber-400">{requirements.filter(r => r.priority === 'medium').length}</p><p className="text-[9px] opacity-40">Medium</p></div>
              </div>
              {['functional', 'non_functional', 'constraint'].map(cat => {
                const count = requirements.filter(r => r.category === cat).length;
                return count > 0 ? <div key={cat} className="flex items-center justify-between bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md px-2.5 py-1.5"><span className="text-xs capitalize">{cat.replace('_', ' ')}</span><Badge variant="outline" className="text-[9px]">{count}</Badge></div> : null;
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function TechStackPreview({ techStack }) {
  const hasAny = Object.values(techStack).some(v => v && v !== 'None');
  return (
    <ScrollArea className="flex-1">
      <div className="p-4">
        {hasAny ? (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold opacity-80">Selected Stack</h3>
            {[{ key: 'frontend', label: 'Frontend', color: 'text-cyan-400' }, { key: 'backend', label: 'Backend', color: 'text-emerald-400' }, { key: 'database', label: 'Database', color: 'text-amber-400' }, { key: 'cloud', label: 'Cloud', color: 'text-indigo-400' }, { key: 'devops', label: 'DevOps', color: 'text-purple-400' }, { key: 'other', label: 'Other', color: 'opacity-60' }]
              .filter(({ key }) => techStack[key] && techStack[key] !== 'None')
              .map(({ key, label, color }) => (
                <div key={key} className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-md p-3"><p className="text-[10px] uppercase tracking-wider opacity-40 mb-1">{label}</p><p className={`text-sm font-medium ${color}`}>{techStack[key]}</p></div>
              ))}
          </div>
        ) : <div className="text-center py-16"><Terminal className="w-10 h-10 opacity-10 mx-auto mb-2" /><p className="opacity-30 text-sm">Select your tech stack</p></div>}
      </div>
    </ScrollArea>
  );
}

function MermaidContent({ content }) {
  if (!content) return null;
  // Use MarkdownRenderer for full Markdown support
  return <MarkdownRenderer content={content} />;
}

/* Executive Summary Panel */
function ExecutiveSummaryPanel({ projectId, project, onRefresh, canEdit }) {
  const [generating, setGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const summary = project?.executive_summary || '';

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter your project idea');
      return;
    }
    setGenerating(true);
    try {
      await projectAPI.generateExecutiveSummary(projectId, { prompt: aiPrompt });
      await onRefresh();
      setShowAIModal(false);
      setAiPrompt('');
      // Start editing the generated content
      const updatedProject = await projectAPI.get(projectId);
      setEditContent(updatedProject.data?.executive_summary || '');
      setIsEditing(true);
      toast.success('Executive summary generated! You can now edit it.');
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await projectAPI.update(projectId, { executive_summary: editContent });
      await onRefresh();
      setIsEditing(false);
      toast.success('Executive summary saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    setEditContent(summary);
    setIsEditing(true);
  };

  const downloadAsDocx = () => {
    if (!summary) return;
    const content = `Executive Summary\n\n${summary}`;
    // Create simple docx-compatible content
    const docxContent = `
<!DOCTYPE html>
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
<head><meta charset="utf-8"><title>Executive Summary</title></head>
<body style="font-family: Calibri, sans-serif;">
<h1>Executive Summary</h1>
${summary.split('\n').map(line => {
  if (line.startsWith('## ')) return `<h2>${line.slice(3)}</h2>`;
  if (line.startsWith('### ')) return `<h3>${line.slice(4)}</h3>`;
  if (line.startsWith('- ')) return `<li>${line.slice(2)}</li>`;
  if (line.trim()) return `<p>${line}</p>`;
  return '<br/>';
}).join('\n')}
</body></html>`;
    
    const blob = new Blob([docxContent], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Executive-Summary.docx';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Simple rich text formatting helpers
  const applyFormat = (format) => {
    const textarea = document.querySelector('#exec-summary-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editContent.substring(start, end);
    
    let newText = '';
    switch (format) {
      case 'bold':
        newText = `**${selectedText}**`;
        break;
      case 'italic':
        newText = `*${selectedText}*`;
        break;
      case 'heading':
        newText = `## ${selectedText}`;
        break;
      case 'bullet':
        newText = selectedText.split('\n').map(line => `- ${line}`).join('\n');
        break;
      default:
        newText = selectedText;
    }
    
    setEditContent(editContent.substring(0, start) + newText + editContent.substring(end));
  };

  return (
    <ScrollArea className="flex-1">
      <div className="p-6" data-testid="executive-summary-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-left">Executive Summary</h2>
            <p className="text-sm opacity-60 text-left">High-level project overview for stakeholders</p>
          </div>
          <div className="flex gap-2">
            {summary && !isEditing && (
              <Button variant="outline" size="sm" onClick={downloadAsDocx}>
                <Download className="w-4 h-4 mr-2" /> Download .docx
              </Button>
            )}
            {canEdit && !isEditing && (
              <>
                {summary && (
                  <Button variant="outline" onClick={startEditing}>
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                )}
                <Button onClick={() => setShowAIModal(true)}>
                  <Bot className="w-4 h-4 mr-2" />
                  {summary ? 'Regenerate with AI' : 'Generate with AI'}
                </Button>
              </>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-3">
            {/* Rich Text Toolbar */}
            <div className="flex gap-1 p-2 bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg">
              <Button variant="ghost" size="sm" onClick={() => applyFormat('bold')} title="Bold">
                <strong>B</strong>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => applyFormat('italic')} title="Italic">
                <em>I</em>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => applyFormat('heading')} title="Heading">
                H2
              </Button>
              <Button variant="ghost" size="sm" onClick={() => applyFormat('bullet')} title="Bullet List">
                • List
              </Button>
            </div>
            <Textarea
              id="exec-summary-editor"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Enter executive summary text here... You can use Markdown formatting."
              rows={18}
              className="text-sm leading-relaxed"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : summary ? (
          <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-6">
            <MarkdownRenderer content={summary} />
          </div>
        ) : (
          <div className="text-center py-16 bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <h3 className="text-lg font-semibold mb-2">No Executive Summary Yet</h3>
            <p className="text-sm opacity-60 mb-4">Generate with AI by entering your project idea, or write manually.</p>
            {canEdit && (
              <Button onClick={() => setShowAIModal(true)}>
                <Bot className="w-4 h-4 mr-2" />
                Generate with AI
              </Button>
            )}
          </div>
        )}

        {/* AI Generation Modal */}
        {showAIModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-[var(--surface-bg)] border border-[var(--surface-border)] rounded-xl w-full max-w-lg p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Generate Executive Summary</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowAIModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm opacity-60 mb-4 text-left">
                Describe your project idea and AI will generate a comprehensive executive summary.
              </p>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Example: A mobile banking app that allows users to manage accounts, transfer money, pay bills, and track expenses with AI-powered insights..."
                rows={6}
                className="mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAIModal(false)}>Cancel</Button>
                <Button onClick={handleGenerateWithAI} disabled={generating || !aiPrompt.trim()}>
                  <Bot className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'Generating...' : 'Generate'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

/* Project Scope Panel */
function ProjectScopePanel({ projectId, project, onRefresh, canEdit }) {
  const SECTIONS = [
    {
      key: 'in_scope',
      label: 'In Scope',
      description: 'Features and functionality that ARE part of this project.',
      placeholder: 'List each in-scope feature on its own line.\nExample:\nUser registration and login\nProduct catalog with search\nShopping cart and checkout',
      icon: CheckCircle2,
      tone: 'emerald',
    },
    {
      key: 'out_of_scope',
      label: 'Out of Scope',
      description: 'Features explicitly NOT included or deferred to a later release.',
      placeholder: 'List each out-of-scope item on its own line.\nExample:\nMobile native apps (iOS/Android)\nMulti-currency support\nAdmin analytics dashboard',
      icon: X,
      tone: 'rose',
    },
    {
      key: 'assumptions',
      label: 'Assumptions',
      description: 'Things being taken for granted for the project to succeed.',
      placeholder: 'List each assumption on its own line.\nExample:\nUsers have modern browsers (Chrome, Safari, Edge)\nStripe will be used for payments\nEnglish is the only supported language at launch',
      icon: ListChecks,
      tone: 'sky',
    },
    {
      key: 'constraints',
      label: 'Constraints',
      description: 'Technical, business, regulatory or resource limitations.',
      placeholder: 'List each constraint on its own line.\nExample:\nMust comply with GDPR\nLaunch within 3 months\nBudget capped at $50k for infrastructure',
      icon: Shield,
      tone: 'amber',
    },
  ];

  const TONE_CLASSES = {
    emerald: { border: 'border-emerald-500/30', text: 'text-emerald-400', ring: 'focus-within:ring-emerald-500/40', dot: 'bg-emerald-400' },
    rose:    { border: 'border-rose-500/30',    text: 'text-rose-400',    ring: 'focus-within:ring-rose-500/40',    dot: 'bg-rose-400' },
    sky:     { border: 'border-sky-500/30',     text: 'text-sky-400',     ring: 'focus-within:ring-sky-500/40',     dot: 'bg-sky-400' },
    amber:   { border: 'border-amber-500/30',   text: 'text-amber-400',   ring: 'focus-within:ring-amber-500/40',   dot: 'bg-amber-400' },
  };

  const scope = project?.project_scope || { in_scope: [], out_of_scope: [], assumptions: [], constraints: [] };
  const changeRequests = project?.scope_change_requests || [];

  // One textarea state per section (as multiline string)
  const buildInitial = React.useCallback(() => ({
    in_scope: (scope.in_scope || []).join('\n'),
    out_of_scope: (scope.out_of_scope || []).join('\n'),
    assumptions: (scope.assumptions || []).join('\n'),
    constraints: (scope.constraints || []).join('\n'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [project?.id]);

  const [drafts, setDrafts] = useState(buildInitial());
  const [savingKey, setSavingKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(null);
  const [aiPromptKey, setAiPromptKey] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('in_scope');

  // Re-sync drafts when project changes (e.g. AI generation or reload)
  useEffect(() => {
    setDrafts({
      in_scope: (scope.in_scope || []).join('\n'),
      out_of_scope: (scope.out_of_scope || []).join('\n'),
      assumptions: (scope.assumptions || []).join('\n'),
      constraints: (scope.constraints || []).join('\n'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.project_scope]);

  const isDirty = (key) => {
    const persisted = (scope[key] || []).join('\n');
    return (drafts[key] || '') !== persisted;
  };

  const saveSection = async (key) => {
    if (!canEdit) return;
    setSavingKey(key);
    try {
      const items = (drafts[key] || '').split('\n').map(s => s.trim()).filter(Boolean);
      await projectAPI.updateProjectScopeSection(projectId, { section: key, items });
      await onRefresh();
      toast.success(`${SECTIONS.find(s => s.key === key).label} saved`);
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  const generateSection = async (key) => {
    if (!canEdit) return;
    setGeneratingKey(key);
    try {
      const res = await projectAPI.generateProjectScopeSection(projectId, {
        section: key,
        prompt: aiPrompt.trim() || undefined,
      });
      const items = res.data?.items || [];
      setDrafts(prev => ({ ...prev, [key]: items.join('\n') }));
      await onRefresh();
      setAiPromptKey(null);
      setAiPrompt('');
      toast.success(`${SECTIONS.find(s => s.key === key).label} generated`);
    } catch (err) {
      toast.error('Generation failed');
    } finally {
      setGeneratingKey(null);
    }
  };

  return (
    <ScrollArea className="flex-1 h-full">
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="project-scope-panel">
        <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-left">Project Scope</h2>
            <p className="text-sm opacity-60 text-left mt-1">
              Define what is in scope, out of scope, your assumptions and constraints. Each section can be generated with AI or written manually.
            </p>
          </div>
          {changeRequests.length > 0 && (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              <AlertOctagon className="w-3 h-3 mr-1" />
              {changeRequests.length} scope change{changeRequests.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Tab Bar */}
        <div className="flex items-center gap-1 border-b border-[var(--surface-border)] mb-5" data-testid="scope-tabs">
          {SECTIONS.map((sec) => {
            const tone = TONE_CLASSES[sec.tone];
            const Icon = sec.icon;
            const count = (drafts[sec.key] || '').split('\n').map(s => s.trim()).filter(Boolean).length;
            const isActive = activeTab === sec.key;
            return (
              <button
                key={sec.key}
                onClick={() => setActiveTab(sec.key)}
                className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  isActive
                    ? `${tone.text} border-current`
                    : 'opacity-60 hover:opacity-100 border-transparent'
                }`}
                data-testid={`scope-tab-${sec.key}`}
              >
                <Icon className="w-4 h-4" />
                <span>{sec.label}</span>
                <Badge variant="outline" className={`text-[10px] ${isActive ? tone.text : ''}`}>{count}</Badge>
              </button>
            );
          })}
        </div>

        {/* Active Tab Content - Full Width */}
        {SECTIONS.filter(s => s.key === activeTab).map((sec) => {
          const tone = TONE_CLASSES[sec.tone];
          const Icon = sec.icon;
          const dirty = isDirty(sec.key);
          const generating = generatingKey === sec.key;
          const saving = savingKey === sec.key;
          const aiOpen = aiPromptKey === sec.key;
          const items = (drafts[sec.key] || '').split('\n').map(s => s.trim()).filter(Boolean);
          return (
            <div key={sec.key} data-testid={`scope-section-${sec.key}`} className="space-y-4">
              {/* Section Header */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${tone.dot}`} />
                    <Icon className={`w-5 h-5 ${tone.text}`} />
                    <h3 className={`text-lg font-semibold ${tone.text} text-left`}>{sec.label}</h3>
                    <span className="text-xs opacity-50">{items.length} item{items.length !== 1 ? 's' : ''}</span>
                  </div>
                  <p className="text-sm opacity-60 text-left">{sec.description}</p>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => { setAiPromptKey(aiOpen ? null : sec.key); setAiPrompt(''); }}
                    disabled={generating}
                    data-testid={`scope-generate-${sec.key}-btn`}
                  >
                    <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                    {generating ? 'Generating...' : 'Generate with AI'}
                  </Button>
                )}
              </div>

              {/* AI Prompt Drawer */}
              {aiOpen && (
                <div className="p-4 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
                  <label className="text-sm font-medium block mb-2 text-left">
                    Optional context for AI <span className="opacity-50">(leave blank to use the project description)</span>
                  </label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={`Anything specific you want the AI to consider for "${sec.label}"...`}
                    rows={3}
                    className="text-sm mb-3"
                    disabled={generating}
                    data-testid={`scope-ai-prompt-${sec.key}`}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setAiPromptKey(null); setAiPrompt(''); }} disabled={generating}>Cancel</Button>
                    <Button onClick={() => generateSection(sec.key)} disabled={generating} data-testid={`scope-ai-run-${sec.key}`}>
                      <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                      {generating ? 'Generating...' : 'Generate'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Full-width write panel */}
              <div className={`bg-[var(--surface-card)] border ${tone.border} rounded-xl p-4 flex flex-col ${tone.ring} focus-within:ring-2 transition`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider opacity-50 font-medium">Write — one item per line</span>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant={dirty ? 'default' : 'outline'}
                      onClick={() => saveSection(sec.key)}
                      disabled={!dirty || saving}
                      data-testid={`scope-save-${sec.key}-btn`}
                    >
                      <Save className="w-3.5 h-3.5 mr-1.5" />
                      {saving ? 'Saving...' : dirty ? 'Save' : 'Saved'}
                    </Button>
                  )}
                </div>
                <Textarea
                  value={drafts[sec.key] || ''}
                  onChange={(e) => setDrafts(prev => ({ ...prev, [sec.key]: e.target.value }))}
                  placeholder={sec.placeholder}
                  disabled={!canEdit || generating}
                  className="text-sm flex-1 min-h-[560px] font-mono resize-none w-full"
                  data-testid={`scope-textarea-${sec.key}`}
                />
              </div>
            </div>
          );
        })}

        {/* Scope Change Alerts */}
        {changeRequests.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-6" data-testid="scope-change-alerts">
            <div className="flex items-center gap-2 mb-3">
              <AlertOctagon className="w-4 h-4 text-amber-400" />
              <h3 className="text-sm font-semibold text-amber-400 text-left">Scope Change Alerts</h3>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">{changeRequests.length}</Badge>
            </div>
            <div className="space-y-2">
              {changeRequests.slice(0, 5).map((cr, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-[var(--surface-bg)] rounded">
                  <span className="text-sm text-left">{cr.title}</span>
                  <Badge variant="outline" className="text-[10px]">{cr.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

/* Architecture Panel with Diagram Generation */
function ArchitecturePanel({ projectId, project, phaseChat, sending, onSendMessage, onRefresh }) {
  const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'diagram'
  const [generating, setGenerating] = useState(false);
  const [generationSteps, setGenerationSteps] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [diagramPrompt, setDiagramPrompt] = useState('');
  
  const architectureDiagram = project?.architecture_diagram || '';
  const requirements = project?.phases?.requirements?.items || [];

  const handleGenerateDiagram = async () => {
    setGenerating(true);
    setGenerationSteps([]);
    
    // Simulate streaming progress
    const steps = [
      'Analyzing project requirements...',
      'Identifying system components...',
      'Mapping service dependencies...',
      'Defining data flow patterns...',
      'Creating architecture layers...',
      'Generating Mermaid diagram...',
    ];
    
    for (const step of steps) {
      await new Promise(r => setTimeout(r, 600));
      setGenerationSteps(prev => [...prev, step]);
    }
    
    try {
      await projectAPI.generateArchitectureDiagram(projectId, { prompt: diagramPrompt });
      await onRefresh();
      setShowGenerateModal(false);
      setDiagramPrompt('');
      setViewMode('diagram');
      toast.success('Architecture diagram generated!');
    } catch (err) {
      toast.error('Failed to generate diagram');
    } finally {
      setGenerating(false);
      setGenerationSteps([]);
    }
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="architecture-panel">
      {/* Header */}
      <div className="p-3 border-b border-[var(--surface-border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'chat' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('chat')}
          >
            <Bot className="w-4 h-4 mr-1.5" /> AI Chat
          </Button>
          <Button
            variant={viewMode === 'diagram' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('diagram')}
          >
            <Network className="w-4 h-4 mr-1.5" /> Architecture Diagram
          </Button>
        </div>
        <Button size="sm" onClick={() => setShowGenerateModal(true)}>
          <Sparkles className="w-4 h-4 mr-1.5" /> Generate Diagram
        </Button>
      </div>

      {/* Content */}
      {viewMode === 'chat' ? (
        <ModernChat
          messages={phaseChat}
          isProcessing={sending}
          placeholder="Ask about architecture patterns, system design..."
          emptyStateTitle="Architecture Phase"
          emptyStateDescription="Discuss system architecture, design patterns, and infrastructure decisions."
          onSendMessage={onSendMessage}
        />
      ) : (
        <ScrollArea className="flex-1">
          <div className="p-6">
            {architectureDiagram ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-left">Project Architecture</h3>
                  <Button variant="outline" size="sm" onClick={() => setShowGenerateModal(true)}>
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate
                  </Button>
                </div>
                <div className="bg-[var(--surface-card)] border border-[var(--surface-border)] rounded-lg p-6 overflow-x-auto">
                  <MermaidDiagram chart={architectureDiagram} />
                </div>
                {/* Show raw diagram code */}
                <details className="text-sm">
                  <summary className="cursor-pointer opacity-60 hover:opacity-100">View Diagram Code</summary>
                  <pre className="mt-2 p-4 bg-[#0d1117] rounded-lg overflow-x-auto text-xs text-white/80">
                    {architectureDiagram}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="text-center py-16">
                <Network className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <h3 className="text-lg font-semibold mb-2">No Architecture Diagram Yet</h3>
                <p className="text-sm opacity-60 mb-6 max-w-md mx-auto">
                  Generate a visual architecture diagram based on your project requirements and tech stack.
                </p>
                <Button onClick={() => setShowGenerateModal(true)}>
                  <Sparkles className="w-4 h-4 mr-2" /> Generate Architecture Diagram
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-bg)] border border-[var(--surface-border)] rounded-xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Generate Architecture Diagram</h3>
              <Button variant="ghost" size="icon" onClick={() => { setShowGenerateModal(false); setGenerationSteps([]); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm opacity-60 mb-4 text-left">
              Describe your system architecture or let AI generate based on your requirements. The diagram will be rendered using Mermaid.
            </p>

            {requirements.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
                <p className="text-xs font-medium mb-2 text-left">Based on {requirements.length} requirements</p>
                <p className="text-xs opacity-60 text-left">AI will analyze your requirements to create an appropriate architecture.</p>
              </div>
            )}
            
            <Textarea
              value={diagramPrompt}
              onChange={(e) => setDiagramPrompt(e.target.value)}
              placeholder="Optional: Describe specific architecture needs...&#10;&#10;Example: Microservices architecture with API Gateway, user service, payment service, and message queue for async processing..."
              rows={4}
              className="mb-4"
              disabled={generating}
            />
            
            {/* Generation Progress */}
            {generationSteps.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs font-medium">Generating architecture...</span>
                </div>
                <div className="space-y-1">
                  {generationSteps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      <span className="opacity-70">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowGenerateModal(false); setGenerationSteps([]); }} disabled={generating}>
                Cancel
              </Button>
              <Button onClick={handleGenerateDiagram} disabled={generating}>
                <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generating...' : 'Generate Diagram'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Code Panel with File Tree */
function CodePanel({ projectId, project, phaseChat, sending, onSendMessage }) {
  const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'files'
  const generatedCode = project?.generated_code || {};
  const sourceControl = project?.source_control;

  const handlePushToGitHub = async () => {
    if (!sourceControl) {
      toast.error('Connect source control first');
      return;
    }
    toast.info('GitHub push feature coming soon');
  };

  // Build file tree from generated code
  const buildFileTree = () => {
    const tree = {};
    Object.entries(generatedCode).forEach(([path, content]) => {
      const parts = path.split('/');
      let current = tree;
      parts.forEach((part, i) => {
        if (i === parts.length - 1) {
          current[part] = { type: 'file', content, path };
        } else {
          current[part] = current[part] || { type: 'folder', children: {} };
          current = current[part].children;
        }
      });
    });
    return tree;
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const toggleFolder = (path) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderTree = (node, path = '', depth = 0) => {
    return Object.entries(node).map(([name, data]) => {
      const fullPath = path ? `${path}/${name}` : name;
      if (data.type === 'folder') {
        const isExpanded = expandedFolders.has(fullPath);
        return (
          <div key={fullPath}>
            <button
              onClick={() => toggleFolder(fullPath)}
              className="w-full flex items-center gap-2 px-2 py-1 hover:bg-[var(--surface-card)] rounded text-left text-sm"
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              <FolderTree className="w-4 h-4 text-amber-400" />
              <span>{name}</span>
            </button>
            {isExpanded && renderTree(data.children, fullPath, depth + 1)}
          </div>
        );
      }
      return (
        <button
          key={fullPath}
          onClick={() => setSelectedFile(data)}
          className={`w-full flex items-center gap-2 px-2 py-1 hover:bg-[var(--surface-card)] rounded text-left text-sm ${selectedFile?.path === data.path ? 'bg-[var(--accent-indigo)]/20 text-[var(--accent-indigo)]' : ''}`}
          style={{ paddingLeft: `${depth * 12 + 8}px` }}
        >
          <Code2 className="w-4 h-4 text-blue-400" />
          <span>{name}</span>
        </button>
      );
    });
  };

  const fileTree = buildFileTree();
  const hasFiles = Object.keys(fileTree).length > 0;

  return (
    <div className="flex-1 flex flex-col" data-testid="code-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'chat' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('chat')}
          >
            <Bot className="w-4 h-4 mr-1.5" /> AI Chat
          </Button>
          <Button
            variant={viewMode === 'files' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('files')}
          >
            <FolderTree className="w-4 h-4 mr-1.5" /> Files {hasFiles && `(${Object.keys(generatedCode).length})`}
          </Button>
        </div>
        {sourceControl && (
          <Button variant="outline" size="sm" onClick={handlePushToGitHub}>
            <GitCommit className="w-4 h-4 mr-1.5" /> Push to GitHub
          </Button>
        )}
      </div>

      {/* Content */}
      {viewMode === 'chat' ? (
        <ModernChat
          messages={phaseChat}
          isProcessing={sending}
          placeholder="Ask AI to generate code..."
          emptyStateTitle="Code Generation"
          emptyStateDescription="Describe what you want to build and AI will generate the code."
          onSendMessage={onSendMessage}
        />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* File Tree */}
          <div className="w-64 border-r border-[var(--surface-border)] overflow-y-auto">
            <div className="p-2">
              {hasFiles ? (
                renderTree(fileTree)
              ) : (
                <div className="text-center py-8 opacity-50">
                  <FolderTree className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No files generated yet</p>
                  <p className="text-xs mt-1">Use AI Chat to generate code</p>
                </div>
              )}
            </div>
          </div>

          {/* File Content */}
          <div className="flex-1 overflow-hidden">
            {selectedFile ? (
              <ScrollArea className="h-full">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-mono opacity-70">{selectedFile.path}</span>
                    <Button variant="ghost" size="sm" onClick={() => {
                      navigator.clipboard.writeText(selectedFile.content);
                      toast.success('Copied to clipboard');
                    }}>
                      Copy
                    </Button>
                  </div>
                  <pre className="bg-[#0d1117] rounded-lg p-4 overflow-x-auto text-sm">
                    <code className="text-white/90">{selectedFile.content}</code>
                  </pre>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex items-center justify-center h-full opacity-50">
                <div className="text-center">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Select a file to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* Management Preview Panel */
function ManagementPreviewPanel({ type, data }) {
  const { tasks = [], epics = [], members = [] } = data;
  
  const taskStats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    review: tasks.filter(t => t.status === 'review').length,
    done: tasks.filter(t => t.status === 'done').length,
    byPriority: {
      critical: tasks.filter(t => t.priority === 'critical').length,
      high: tasks.filter(t => t.priority === 'high').length,
      medium: tasks.filter(t => t.priority === 'medium').length,
      low: tasks.filter(t => t.priority === 'low').length,
    }
  };

  const bugTasks = tasks.filter(t => t.task_type === 'bug');
  const bugStats = {
    total: bugTasks.length,
    open: bugTasks.filter(b => b.status === 'todo' || b.status === 'open').length,
    resolved: bugTasks.filter(b => b.status === 'done' || b.status === 'resolved').length,
  };

  const epicStats = {
    total: epics.length,
    planned: epics.filter(e => e.status === 'planned').length,
    in_progress: epics.filter(e => e.status === 'in_progress').length,
    completed: epics.filter(e => e.status === 'completed').length,
  };

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 space-y-4">
        {type === 'tasks' && (
          <>
            <div className="text-center mb-6">
              <LayoutGrid className="w-8 h-8 mx-auto mb-2 text-[var(--accent-indigo)] opacity-60" />
              <h3 className="text-base font-semibold">Task Overview</h3>
              <p className="text-sm opacity-50">Sprint progress at a glance</p>
            </div>
            
            {/* Task Distribution */}
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <p className="text-sm font-medium mb-3">Status Distribution</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-2 rounded bg-slate-500/10">
                  <div className="w-3 h-3 rounded-full bg-slate-500" />
                  <span className="text-sm">To Do</span>
                  <span className="ml-auto font-semibold">{taskStats.todo}</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-blue-500/10">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-sm">In Progress</span>
                  <span className="ml-auto font-semibold">{taskStats.in_progress}</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-amber-500/10">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm">Review</span>
                  <span className="ml-auto font-semibold">{taskStats.review}</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-emerald-500/10">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm">Done</span>
                  <span className="ml-auto font-semibold">{taskStats.done}</span>
                </div>
              </div>
            </div>
            
            {/* Priority Breakdown */}
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <p className="text-sm font-medium mb-3">Priority Breakdown</p>
              <div className="space-y-2">
                {taskStats.byPriority.critical > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-400">Critical</span>
                    <div className="flex-1 h-2 bg-[var(--surface-border)] rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${(taskStats.byPriority.critical / taskStats.total) * 100}%` }} />
                    </div>
                    <span className="text-sm font-medium">{taskStats.byPriority.critical}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-orange-400">High</span>
                  <div className="flex-1 h-2 bg-[var(--surface-border)] rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(taskStats.byPriority.high / Math.max(taskStats.total, 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium">{taskStats.byPriority.high}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-amber-400">Medium</span>
                  <div className="flex-1 h-2 bg-[var(--surface-border)] rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(taskStats.byPriority.medium / Math.max(taskStats.total, 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium">{taskStats.byPriority.medium}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">Low</span>
                  <div className="flex-1 h-2 bg-[var(--surface-border)] rounded-full overflow-hidden">
                    <div className="h-full bg-slate-500 rounded-full" style={{ width: `${(taskStats.byPriority.low / Math.max(taskStats.total, 1)) * 100}%` }} />
                  </div>
                  <span className="text-sm font-medium">{taskStats.byPriority.low}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {type === 'bugs' && (
          <>
            <div className="text-center mb-6">
              <Bug className="w-8 h-8 mx-auto mb-2 text-red-400 opacity-60" />
              <h3 className="text-base font-semibold">Bug Overview</h3>
              <p className="text-sm opacity-50">Issue tracking summary</p>
            </div>
            
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded bg-red-500/10">
                  <p className="text-2xl font-bold text-red-400">{bugStats.open}</p>
                  <p className="text-sm opacity-60">Open</p>
                </div>
                <div className="p-3 rounded bg-blue-500/10">
                  <p className="text-2xl font-bold text-blue-400">{bugStats.total - bugStats.open - bugStats.resolved}</p>
                  <p className="text-sm opacity-60">In Progress</p>
                </div>
                <div className="p-3 rounded bg-emerald-500/10">
                  <p className="text-2xl font-bold text-emerald-400">{bugStats.resolved}</p>
                  <p className="text-sm opacity-60">Resolved</p>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <p className="text-sm font-medium text-amber-400 mb-1">Tip</p>
              <p className="text-sm opacity-70">Use severity levels (Critical, High, Medium, Low) to prioritize bug fixes effectively.</p>
            </div>
          </>
        )}

        {type === 'roadmap' && (
          <>
            <div className="text-center mb-6">
              <Milestone className="w-8 h-8 mx-auto mb-2 text-purple-400 opacity-60" />
              <h3 className="text-base font-semibold">Roadmap Overview</h3>
              <p className="text-sm opacity-50">Epic progress tracking</p>
            </div>
            
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded bg-slate-500/10">
                  <p className="text-2xl font-bold">{epicStats.planned}</p>
                  <p className="text-sm opacity-60">Planned</p>
                </div>
                <div className="p-3 rounded bg-blue-500/10">
                  <p className="text-2xl font-bold text-blue-400">{epicStats.in_progress}</p>
                  <p className="text-sm opacity-60">Active</p>
                </div>
                <div className="p-3 rounded bg-emerald-500/10">
                  <p className="text-2xl font-bold text-emerald-400">{epicStats.completed}</p>
                  <p className="text-sm opacity-60">Completed</p>
                </div>
              </div>
            </div>
            
            {epics.length > 0 && (
              <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
                <p className="text-sm font-medium mb-3">Epic Timeline</p>
                <div className="space-y-2">
                  {epics.slice(0, 5).map(epic => {
                    const epicTasks = tasks.filter(t => t.epic_id === epic.id);
                    const progress = epicTasks.length > 0 ? Math.round((epicTasks.filter(t => t.status === 'done').length / epicTasks.length) * 100) : 0;
                    return (
                      <div key={epic.id} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: epic.color || '#6366f1' }} />
                        <span className="text-sm flex-1 truncate">{epic.title}</span>
                        <span className="text-xs opacity-50">{progress}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {type === 'members' && (
          <>
            <div className="text-center mb-6">
              <Users className="w-8 h-8 mx-auto mb-2 text-[var(--accent-indigo)] opacity-60" />
              <h3 className="text-base font-semibold">Team Overview</h3>
              <p className="text-sm opacity-50">{members.length} team member{members.length !== 1 ? 's' : ''}</p>
            </div>
            
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <p className="text-sm font-medium mb-3">Role Distribution</p>
              <div className="space-y-2">
                {['owner', 'admin', 'developer', 'designer', 'qa', 'ba', 'viewer'].map(role => {
                  const count = members.filter(m => m.role === role).length;
                  if (count === 0) return null;
                  const colors = { owner: 'amber', admin: 'purple', developer: 'blue', designer: 'pink', qa: 'emerald', ba: 'cyan', viewer: 'slate' };
                  return (
                    <div key={role} className="flex items-center gap-2">
                      <span className={`text-sm capitalize text-${colors[role]}-400`}>{role === 'ba' ? 'Business Analyst' : role}</span>
                      <div className="flex-1" />
                      <span className="text-sm font-medium">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="bg-[var(--accent-indigo)]/10 border border-[var(--accent-indigo)]/20 rounded-lg p-4">
              <p className="text-sm font-medium text-[var(--accent-indigo)] mb-1">RBAC Enabled</p>
              <p className="text-sm opacity-70">Team members see only the features their role permits.</p>
            </div>
          </>
        )}

        {type === 'jira' && (
          <>
            <div className="text-center mb-6">
              <svg className="w-8 h-8 mx-auto mb-2 text-blue-400 opacity-60" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.005 1.005 0 0 0 23.013 0z"/>
              </svg>
              <h3 className="text-base font-semibold">Jira Integration</h3>
              <p className="text-sm opacity-50">Sync with Atlassian Jira</p>
            </div>
            
            <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-4">
              <p className="text-sm font-medium mb-3">Sync Features</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                  Tasks → Jira Issues
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  Bugs → Jira Bugs
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                  Epics → Jira Epics
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Two-way status sync
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  );
}
